/**
 * Sprint C3a — Invite User Modal (admin-only, opened from TeamPage).
 *
 * Form mit Email/DisplayName/Role-Select. Submit → POST /api/admin/users.
 *
 * Sprint UI-D2b: auf das `Modal`-Primitive migriert (Portal, Focus-Trap,
 * Esc — vorher fehlte Esc komplett). `disableClose={submitting}` sperrt Close
 * während des Requests — bewusste Verhaltens-Angleichung an die übrigen
 * Form-Modals (vorher schloss der Backdrop währenddessen). Footer-Submit-Button
 * referenziert die `<form>` per `form`-Attribut → Enter-to-submit bleibt.
 *
 * 409-Handling (Codex R1 A13 + R2 F6):
 * - response.deactivated === true → secondary-CTA "Reaktivieren?" anzeigen.
 * - 409 ohne deactivated-flag → simpler error-text "Email bereits registriert".
 */
import { useId, useState } from 'react';
import type { AdminUserRow, Role } from '../../shared/admin-user-types';
import { api, ApiError } from '../lib/api';
import { normalizeEmail } from '../../shared/email-normalize';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { FIELD_LABEL_CLASS, FIELD_INPUT_CLASS } from './ui/form-field';

interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
  /** Current list of users (used for 409-deactivated-recovery resolve-by-email). */
  existingUsers: AdminUserRow[];
  /** Callback invoked when user clicks "Reaktivieren?" on a 409-deactivated. */
  onReactivate: (existingUserId: string) => void;
  /** Refetch the user-list once on stale-list-miss (Codex R2 F6 + PR-R2 [P2]). */
  onRefetchUsers: () => Promise<AdminUserRow[]>;
}

interface ConflictState {
  message: string;
  deactivated: boolean;
  /** Email that hit the conflict (for stale-list recovery). */
  conflictedEmail: string;
}

export function InviteUserModal({
  onClose,
  onSuccess,
  existingUsers,
  onReactivate,
  onRefetchUsers,
}: InviteUserModalProps) {
  const formId = `invite-form-${useId()}`;
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('Member');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setConflict(null);

    const trimmedEmail = email.trim();
    const trimmedName = displayName.trim();
    if (!trimmedEmail || !trimmedName) {
      setError('Email und Name sind Pflicht');
      return;
    }

    setSubmitting(true);
    try {
      await api('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: trimmedEmail,
          displayName: trimmedName,
          role,
        }),
      });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const payload = err.body as { error?: string; deactivated?: boolean } | null;
        if (payload?.deactivated) {
          setConflict({
            message: payload.error ?? 'Diese E-Mail ist deaktiviert.',
            deactivated: true,
            conflictedEmail: normalizeEmail(trimmedEmail),
          });
        } else {
          setError(payload?.error ?? 'Email bereits registriert');
        }
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Einladung fehlgeschlagen');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivateClick = async () => {
    if (!conflict) return;
    // Try local lookup first (cheap, no roundtrip if list is fresh).
    const local = existingUsers.find(
      (u) => normalizeEmail(u.email) === conflict.conflictedEmail,
    );
    if (local) {
      onReactivate(local.id);
      return;
    }
    // Stale-list-fallback (Codex R2 F6 + PR-R2 [P2]): refetch list ONCE,
    // retry lookup. If still miss → genuine stale-list error.
    setSubmitting(true);
    try {
      const fresh = await onRefetchUsers();
      const refound = fresh.find(
        (u) => normalizeEmail(u.email) === conflict.conflictedEmail,
      );
      if (refound) {
        onReactivate(refound.id);
        return;
      }
      setError('Liste veraltet. Bitte Modal schließen und Seite neu laden.');
    } catch {
      setError('Liste konnte nicht aktualisiert werden. Bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="User einladen"
      disableClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={submitting}>
            Einladung senden
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-4">
        <div>
          <label
            className={FIELD_LABEL_CLASS}
            htmlFor="invite-email"
          >
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
            autoComplete="off"
            className={FIELD_INPUT_CLASS}
          />
        </div>
        <div>
          <label
            className={FIELD_LABEL_CLASS}
            htmlFor="invite-name"
          >
            Name
          </label>
          <input
            id="invite-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            disabled={submitting}
            maxLength={100}
            className={FIELD_INPUT_CLASS}
          />
        </div>
        <div>
          <label
            className={FIELD_LABEL_CLASS}
            htmlFor="invite-role"
          >
            Rolle
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={submitting}
            className={FIELD_INPUT_CLASS}
          >
            <option value="Member">Member</option>
            <option value="Admin">Admin</option>
          </select>
        </div>

        {error !== null && (
          <div
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {conflict !== null && (
          <div
            role="alert"
            className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
          >
            <p>{conflict.message}</p>
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleReactivateClick}
              >
                Reaktivieren?
              </Button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
